import { useParams, useNavigate } from "react-router-dom";
import { Button, Spinner, Skeleton } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";

import { formatCurrency, formatDate, formatThousands } from "@/utils";

const SalesOrderPrintBill = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data, isLoading, error } = useFetchData(`sales-orders/${id}/show`);
    const { data: receiptSettings, isLoading: loadingReceipt } = useFetchData("app-settings/receipt");

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate(`/sales-order/${id}/detail`)}
                >
                    Kembali ke Detail
                </Button>
            </div>
        );
    }

    const order = data;
    const items = order?.items || [];

    const grandTotal = items.reduce((sum, item) => {
        const subtotal = item.quantity * item.price - (item.discount || 0);
        return sum + subtotal;
    }, 0);

    return (
        <div className="mx-auto max-w-[80mm] text-black">
            {/* Action bar - hidden on print */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate(`/sales-order/${id}/detail`)}
                >
                    Kembali ke Detail
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title = `Bill_${order?.order_number || "Penjualan"}`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print styles */}
            <style>{`
                @media print {
                    @page {
                        size: 80mm auto;
                        margin: 0;
                    }
                    html, body {
                        width: 80mm;
                        margin: 0;
                        padding: 0;
                    }
                }
            `}</style>

            {/* Bill content */}
            <div className="px-1 py-4 font-mono text-[13px] leading-normal print:px-0 print:py-0">
                {/* Header */}
                <div className="mb-3 text-center">
                    {loadingReceipt ? (
                        <div className="flex flex-col items-center gap-1">
                            <Skeleton className="h-4 w-24 rounded" />
                            <Skeleton className="h-3 w-32 rounded" />
                            <Skeleton className="h-3 w-36 rounded" />
                        </div>
                    ) : (
                        <>
                            <p className="text-sm font-bold">{receiptSettings?.title || "RECEIPT"}</p>
                            <p>{receiptSettings?.store_name || ""}</p>
                            <p>{receiptSettings?.store_location || ""}</p>
                        </>
                    )}
                </div>

                <div className="my-3 border-t border-dashed border-black" />

                {/* Order Info */}
                <div className="mb-1 space-y-1">
                    <div className="flex justify-between">
                        <span>No.</span>
                        <span>{order?.order_number}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Tanggal</span>
                        <span>{formatDate(order?.order_date)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Customer</span>
                        <span>{order?.customer?.name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                        <span>Metode Bayar</span>
                        <span>{order?.payment_method?.name || "-"}</span>
                    </div>
                </div>

                <div className="my-3 border-t border-dashed border-black" />

                {/* Items */}
                <div className="space-y-2">
                    {items.map((item, index) => {
                        const subtotal =
                            item.quantity * item.price - (item.discount || 0);
                        return (
                            <div key={item.id}>
                                <p>
                                    {index + 1}. {item.alias || item.material?.name || "-"}
                                </p>
                                <div className="flex justify-between pl-3">
                                    <span>
                                        {formatThousands(item.quantity)} {item.unit?.name || ""} x {formatCurrency(item.price)}
                                    </span>
                                    <span>{formatCurrency(subtotal)}</span>
                                </div>
                                {(item.discount || 0) > 0 && (
                                    <div className="flex justify-between pl-3">
                                        <span>Diskon</span>
                                        <span>-{formatCurrency(item.discount)}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="my-3 border-t border-dashed border-black" />

                {/* Total */}
                <div className="flex justify-between text-sm font-bold">
                    <span>TOTAL</span>
                    <span>{formatCurrency(grandTotal)}</span>
                </div>

                {/* PAID */}
                <div className="my-3 text-center text-sm font-bold">
                    PAID
                </div>

                <div className="my-3 border-t border-dashed border-black" />

                {/* Footer */}
                <div className="text-center text-[12px]">
                    <p>
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                    {loadingReceipt ? (
                        <Skeleton className="mx-auto mt-1 h-3 w-48 rounded" />
                    ) : (
                        <p className="mt-1">{receiptSettings?.footer || ""}</p>
                    )}
                </div>

                <div className="my-3 border-t border-dashed border-black" />

                {/* Disclaimer */}
                <div className="text-center text-[11px]">
                    {loadingReceipt ? (
                        <Skeleton className="mx-auto h-3 w-56 rounded" />
                    ) : (
                        <p>{receiptSettings?.disclaimer || ""}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SalesOrderPrintBill;
